import React, { useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import { toast } from 'react-toastify'
import Select from 'react-select'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import { FaSave, FaSpinner, FaCheck, FaTimes, FaEye, FaCheckCircle, FaTimesCircle } from 'react-icons/fa'
import { useParams, useNavigate } from 'react-router-dom'
import 'react-toastify/dist/ReactToastify.css'
import '../../HR Manager/screens/EmployeeGoals.css'
import { useLocation } from 'react-router-dom';
import API_BASE_URL from '../../../config/api'
import { RiFilePdfLine } from 'react-icons/ri'
import {Link} from 'react-router-dom'

const HREmployeesPendingGoals = () => {
    const baseUrl = API_BASE_URL
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation();
    const employeeId = location.state?.employeeId || id; // fallback to URL param
    const [loading, setLoading] = useState(false)
    const [goals, setGoals] = useState([])
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 20;
    const [expandedTexts, setExpandedTexts] = useState({})
    const [modalOpen, setModalOpen] = useState(false)
    const [selectedGoal, setSelectedGoal] = useState(null)
    const [approvalLoading, setApprovalLoading] = useState(null); // goal id or null
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectGoal, setRejectGoal] = useState(null);
    const [rejectSubmitting, setRejectSubmitting] = useState(false);
    const [rejectError, setRejectError] = useState(null);

    const handleViewGoal = (goal) => {
        setSelectedGoal(goal)
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setSelectedGoal(null)
    }

    const toggleTextExpansion = (goalId, field) => {
        setExpandedTexts(prev => ({
            ...prev,
            [`${goalId}-${field}`]: !prev[`${goalId}-${field}`]
        }))
    }

    const renderTruncatedText = (text, goalId, field, maxLength = 40) => {
        const safeText = (text ?? '').toString();
        if (!safeText) return '';
        if (safeText.length <= maxLength) return safeText;

        const key = `${goalId || 'unknown'}-${field}`;
        const isExpanded = !!expandedTexts[key];
        const shownText = isExpanded ? safeText : `${safeText.slice(0, maxLength)}...`;

        return (
            <span style={{ display: 'inline' }}>
                <span style={{ whiteSpace: 'pre-wrap' }}>{shownText}</span>
                <button
                    type="button"
                    onClick={() => toggleTextExpansion(goalId || 'unknown', field)}
                    style={{
                        marginLeft: 8,
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: '#2563eb',
                        fontWeight: 600,
                        fontSize: 12,
                        textDecoration: 'underline',
                    }}
                >
                    {isExpanded ? 'Read less' : 'Read more'}
                </button>
            </span>
        );
    }

    const handleApproveGoal = async (goal) => {
        setApprovalLoading(goal.id);
        try {
            const token = Cookies.get('token');
            await axios.post(`${baseUrl}/api/goals/${goal.id}/approval/`, { action: 'approve' }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            toast.success('Goal approved!');
            // Refresh goals
            const response = await axios.get(`${baseUrl}/api/goals/pending/`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const pendingGoals = Array.isArray(response.data) ? response.data.filter(goal => goal.approval_status === 'pending') : [];
            setGoals(pendingGoals);
        } catch (err) {
            console.log(err)
            toast.error(err.message || 'Failed to approve goal');
        } finally {
            setApprovalLoading(null);
        }
    };
    const openRejectModal = (goal) => {
        setRejectGoal(goal);
        setRejectReason('');
        setRejectError(null);
        setRejectModalOpen(true);
    };
    const closeRejectModal = () => {
        setRejectModalOpen(false);
        setRejectGoal(null);
        setRejectReason('');
        setRejectError(null);
    };
    const handleRejectSubmit = async (e) => {
        e.preventDefault();
        setRejectSubmitting(true);
        setRejectError(null);
        try {
            const token = Cookies.get('token');
            await axios.post(`${baseUrl}/api/goals/${rejectGoal.id}/approval/`, { action: 'reject', reason: rejectReason }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            toast.success('Goal rejected!');
            // Refresh goals
            const response = await axios.get(`${baseUrl}/api/goals/pending/`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const pendingGoals = Array.isArray(response.data) ? response.data.filter(goal => goal.approval_status === 'pending') : [];
            setGoals(pendingGoals);
            closeRejectModal();
        } catch (err) {

            setRejectError(err.message || 'Failed to reject goal');
        } finally {
            setRejectSubmitting(false);
        }
    };

    useEffect(() => {
        const fetchPendingGoals = async () => {
            setLoading(true)
            try {
                const token = Cookies.get('token')
                const response = await axios.get(`${baseUrl}/api/goals/pending/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                })
                // Only keep goals with approval_status === 'pending'
                const pendingGoals = Array.isArray(response.data) ? response.data.filter(goal => goal.approval_status === 'pending') : [];
                setGoals(pendingGoals)
            } catch (err) {
                toast.error('Failed to fetch pending goals.')
            } finally {
                setLoading(false)
            }
        }
        fetchPendingGoals()
    }, [])

    // Reset to first page whenever goals list changes
    useEffect(() => {
        setCurrentPage(1);
    }, [goals]);

    const totalPages = Math.ceil(goals.length / rowsPerPage) || 1;
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentGoals = goals.slice(indexOfFirstRow, indexOfLastRow);

    const getKraLabel = (goal) => {
        const name = goal?.key_result_area?.name || goal?.name || goal?.key_result_area;
        return (name ?? 'Uncategorized').toString();
    };

    const sortedCurrentGoals = [...currentGoals].sort((a, b) => {
        const an = (a?.employee_name || '').toString();
        const bn = (b?.employee_name || '').toString();
        const empCompare = an.localeCompare(bn, undefined, { sensitivity: 'base' });
        if (empCompare !== 0) return empCompare;

        const ak = getKraLabel(a);
        const bk = getKraLabel(b);
        const kraCompare = ak.localeCompare(bk, undefined, { sensitivity: 'base' });
        if (kraCompare !== 0) return kraCompare;

        const aa = (a?.activity || '').toString();
        const ba = (b?.activity || '').toString();
        return aa.localeCompare(ba, undefined, { sensitivity: 'base' });
    });

    const currentPageCountsByEmployee = sortedCurrentGoals.reduce((acc, g) => {
        const key = (g?.employee_name || 'Unknown Employee').toString();
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        setCurrentPage(newPage);
    };

    return (
        <>
            <Topbar />
            <Sidebar />
            <div className="employee-goals-container">
                <div className="hr-content-wrapper">
                <p style={{fontSize: '16px', fontWeight: '400', color: '#64748b'}}>View all pending KPIs for your department.</p>

                    {loading ? (
                        <div className="loading-state"><FaSpinner className="spinner" /> Loading pending KPIs...</div>
                    ) : (
                        <>
                            <div className="goals-table-wrapper">
                                <table className="goals-table">
                                    <thead>
                                        <tr>
                                            <th>Activity</th>
                                            <th>Stakeholder</th>
                                            <th>RAG</th>
                                            <th>Quarter</th>
                                            <th>Metric</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {goals.length === 0 ? (
                                            <tr><td colSpan={8} className="empty-row">No pending KPIs found.</td></tr>
                                        ) : (
                                            sortedCurrentGoals.map((goal, idx) => {
                                                const employeeName = (goal?.employee_name || 'Unknown Employee').toString();
                                                const prevEmployeeName = idx > 0
                                                    ? (sortedCurrentGoals[idx - 1]?.employee_name || 'Unknown Employee').toString()
                                                    : null;
                                                const showEmployeeHeader = idx === 0 || employeeName !== prevEmployeeName;

                                                const kraLabel = getKraLabel(goal);
                                                const prevKraLabel = idx > 0 ? getKraLabel(sortedCurrentGoals[idx - 1]) : null;
                                                const showKraHeader = showEmployeeHeader || kraLabel !== prevKraLabel;

                                                return (
                                                    <React.Fragment key={goal.id || `${employeeName}-${idx}`}>
                                                        {showEmployeeHeader && (
                                                            <tr>
                                                                <td
                                                                    colSpan={8}
                                                                    style={{
                                                                        background: '#f1f5f9',
                                                                        color: '#0f172a',
                                                                        fontWeight: 700,
                                                                        padding: '12px 10px',
                                                                        borderTop: '1px solid #e2e8f0'
                                                                    }}
                                                                >
                                                                    {employeeName}
                                                                    <span style={{ marginLeft: 10, fontWeight: 600, color: '#475569', fontSize: 12 }}>
                                                                        ({currentPageCountsByEmployee[employeeName]} pending)
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        )}

                                                        {showKraHeader && (
                                                            <tr>
                                                                <td
                                                                    colSpan={8}
                                                                    style={{
                                                                        background: '#ffffff',
                                                                        color: '#0f172a',
                                                                        fontWeight: 700,
                                                                        padding: '10px 10px',
                                                                        borderTop: '1px dashed #e2e8f0'
                                                                    }}
                                                                >
                                                                    {(() => {
                                                                        const kraPercentage =
                                                                            goal?.key_result_area?.percentage ??
                                                                            goal?.kra?.percentage ??
                                                                            goal?.percentage;
                                                                        return (
                                                                            <>
                                                                                {kraLabel}
                                                                                {' '}
                                                                            <span style={{ fontWeight: 800, color: '#475569' }}>
                                                                            (KRA Weight: {kraPercentage !== undefined && kraPercentage !== null ? `${kraPercentage}%` : '-'})
                                                                        </span>
                                                                    </>
                                                                );
                                                            })()}
                                                                </td>
                                                            </tr>
                                                        )}

                                                        <tr>
                                                    
                                                    <td>
                                                        {renderTruncatedText(
                                                            goal.activity,
                                                            goal.id || `${idx + indexOfFirstRow}`,
                                                            'activity',
                                                            55
                                                        )}
                                                    </td>
                                                    <td>{goal.stakeholder}</td>
                                                    <td>
                                                        <div className="rag-bar-bg">
                                                            <div
                                                                className="rag-bar-fill"
                                                                style={{ backgroundColor: goal.progress_indicator, width: '100%' }}
                                                            >
                                                                <span className="rag-bar-label">{goal.rag_color || ''}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{goal.quarter}</td>
                                                    <td>{goal.metric}%</td>
                                                    <td>
                                                        <span className={`status-badge status-${goal.approval_status?.toLowerCase() || 'unknown'}`}>
                                                            {goal.approval_status === 'pending' && <FaSpinner className="pending-spinner" />}
                                                            {goal.approval_status ? goal.approval_status.charAt(0).toUpperCase() + goal.approval_status.slice(1) : ''}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="goal-actions">
                                                            <button className="action-button view-button" title="View" onClick={() => handleViewGoal(goal)}>
                                                                <FaEye />
                                                            </button>
                                                            {goal.approval_status === 'pending' && (
                                                                <>
                                                                    <button className="action-button approve-button" title="Approve" onClick={() => handleApproveGoal(goal)} disabled={approvalLoading === goal.id}>
                                                                        {approvalLoading === goal.id ? <FaSpinner className="pending-spinner" /> : <FaCheckCircle style={{ color: '#22c55e' }} />}
                                                                    </button>
                                                                    <button className="action-button reject-button" title="Reject" onClick={() => openRejectModal(goal)}>
                                                                        <FaTimesCircle style={{ color: '#ef4444' }} />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                        </tr>
                                                    </React.Fragment>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                                {goals.length > rowsPerPage && (
                                    <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', fontSize: 13 }}>
                                        <div>
                                            Showing {indexOfFirstRow + 1}-
                                            {Math.min(indexOfLastRow, goals.length)} of {goals.length} goals
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <button
                                                type="button"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                style={{
                                                    padding: '6px 10px',
                                                    fontSize: 13,
                                                    borderRadius: 6,
                                                    border: '1px solid #e2e8f0',
                                                    background: currentPage === 1 ? '#e5e7eb' : '#ffffff',
                                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                Previous
                                            </button>
                                            <span style={{ fontSize: 13 }}>
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                                style={{
                                                    padding: '6px 10px',
                                                    fontSize: 13,
                                                    borderRadius: 6,
                                                    border: '1px solid #e2e8f0',
                                                    background: currentPage === totalPages ? '#e5e7eb' : '#ffffff',
                                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {modalOpen && selectedGoal && (
                <div className="modal-overlay">
                    <div className="modal-content-employee-goal">
                        <div className="modal-header">
                            <h2>KPI Details</h2>
                            <button onClick={closeModal} className="modal-close-btn">&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="goal-details-grid">
                                <div className="goal-detail-row">
                                    <label>Key Result Area:</label>
                                    <span>{selectedGoal.key_result_area?.name || selectedGoal.name}</span>
                                </div>
                                <div className="goal-detail-row">
                                    <label>Key Result Area Weight:</label>
                                    <span>{selectedGoal.key_result_area?.percentage || selectedGoal.percentage}%</span>
                                </div>
                                <div className="goal-detail-row">
                                    <label>Activity:</label>
                                    <span>{selectedGoal.activity}</span>
                                </div>
                                <div className="goal-detail-row">
                                    <label>KPI Metric:</label>
                                    <span>{selectedGoal.metric}%</span>
                                </div>
                                <div className="goal-detail-row">
                                    <label>Stakeholder:</label>
                                    <span>{selectedGoal.stakeholder}</span>
                                </div>
                                
                                <div className="goal-detail-row">
                                    <label>Quarter:</label>
                                    <span>{selectedGoal.quarter}</span>
                                </div>
                                <div className="goal-detail-row">
                                    <label>RAG Color:</label>
                                    <div className="progress-indicator-display" style={{ backgroundColor: selectedGoal.progress_indicator || '#e5e7eb', width: '100%' }}>

                                    </div>
                                </div>
                                <div className="goal-detail-row">
                                    <label>Completion Status:</label>
                                    <span>
                                        {selectedGoal.status
                                            ? selectedGoal.status.charAt(0).toUpperCase() + selectedGoal.status.slice(1).replace(/_/g, ' ')
                                            : 'N/A'}
                                    </span>
                                </div>
                                <div className="goal-detail-row">
                                    <label>Is Overdue:</label>
                                    <span className={selectedGoal.is_overdue ? 'overdue-yes' : 'overdue-no'}>
                                        {selectedGoal.is_overdue ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                <div className="goal-detail-row">
                                    <label>Approval Status:</label>
                                    <span style={{ color: '#ffc' }} className={`status-badge status-${selectedGoal.approval_status?.toLowerCase() || 'unknown'}`}>
                                        {selectedGoal.approval_status === 'pending' && <FaSpinner className="pending-spinner" />}
                                        {selectedGoal.approval_status ? selectedGoal.approval_status.charAt(0).toUpperCase() + selectedGoal.approval_status.slice(1) : 'N/A'}
                                    </span>
                                </div>
                                <div className="goal-detail-row">
                                    <label>Due Date:</label>
                                    <span>
                                        {selectedGoal.completion_date
                                            ? new Date(selectedGoal.completion_date).toLocaleDateString('en-GB')
                                            : 'N/A'}
                                    </span>
                                </div>
                                {selectedGoal.approval_status === 'rejected' && selectedGoal.rejected_reason && (
                                    <div className="goal-detail-row full-width">
                                        <label>Rejection Reason:</label>
                                        <span className="rejection-reason">{selectedGoal.rejected_reason}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button onClick={closeModal} className="modal-close-button">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {rejectModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content-employee-goal" style={{ maxWidth: 480 }}>
                        <div className="modal-header">
                            <h2>Reject KPI</h2>
                            <button onClick={closeRejectModal} className="modal-close-btn">&times;</button>
                        </div>
                        <form onSubmit={handleRejectSubmit} className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            <div className="goal-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                                <label style={{ fontWeight: 600, color: '#374151', marginBottom: 2 }}>Rejection Reason <span className="required">*</span></label>
                                <textarea
                                    name="rejection_reason"
                                    value={rejectReason}
                                    onChange={e => setRejectReason(e.target.value)}
                                    required
                                    className="form-input"
                                    placeholder="Enter reason for rejection..."
                                    rows={4}
                                    disabled={rejectSubmitting}
                                    style={{ borderRadius: 8, border: '2px solid #e5e7eb', fontSize: 16, padding: '12px 16px', minWidth: '90%' }}
                                />
                            </div>
                            {rejectError && <div className="error-state">{rejectError}</div>}
                            <div className="modal-actions">
                                <button
                                    type="submit"
                                    className="submit-button"
                                    style={{ width: '100%', fontWeight: 700, fontSize: 17, borderRadius: 8, padding: '14px 0', marginTop: 8, background: 'linear-gradient(135deg,#ef4444 0%, #f87171 100%)', color: '#fff' }}
                                    disabled={rejectSubmitting}
                                >
                                    {rejectSubmitting ? 'Rejecting...' : 'Reject KPI'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    )
}

export default HREmployeesPendingGoals
